import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import patternService from '../services/pattern.service';
import courseService from '../services/course.service';
import videoService from '../services/video.service';
import { Course, Video, ExercisePattern, PatternRedactionElement } from '../types';
import SkillSelectionModal from './SkillSelectionModal';
import criterionService from '../services/criterion.service';
import CriterionLevelSelect from './CriterionLevelSelect';

interface Props {
  pattern: ExercisePattern;
  readOnly?: boolean;
  onPatternFlagsChange: (flags: {
    has_calculation_zones?: boolean;
    check_equation_chains?: boolean;
    check_line_equivalence?: boolean;
  }) => Promise<void> | void;
}

const PatternRedactionElementsSection: React.FC<Props> = ({ pattern, readOnly = false, onPatternFlagsChange }) => {
  const [elements, setElements] = useState<PatternRedactionElement[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [skillModalElementId, setSkillModalElementId] = useState<number | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);

  const loadElements = useCallback(async () => {
    if (!pattern?.id) return;
    setLoading(true);
    try {
      const data = await patternService.getRedactionElements(pattern.id);
      setElements(data);
    } catch (e) {
      toast.error("Erreur lors du chargement des éléments de rédaction");
    } finally {
      setLoading(false);
    }
  }, [pattern?.id]);

  useEffect(() => {
    loadElements();
  }, [loadElements]);

  const [inherited, setInherited] = useState<PatternRedactionElement[]>([]);

  useEffect(() => {
    if (!pattern?.id) return;
    criterionService
      .effectiveForPattern(pattern.id)
      .then((all) => setInherited(all.filter((c) => c.owner_skill !== null)))
      .catch(() => setInherited([]));
  }, [pattern?.id]);

  useEffect(() => {
    // Load courses and videos lazily for the pickers
    courseService.getCourses().then(setCourses).catch(() => setCourses([]));
    videoService.getAll().then(setVideos).catch(() => setVideos([]));
  }, []);

  const handleAdd = async () => {
    if (readOnly) return;
    try {
      const created = await patternService.createRedactionElement({
        pattern: pattern.id,
        description: '',
        level: 'expected',
        grader_hint: '',
        example: '',
        python_function: '',
        skills: [],
        order: elements.length,
      });
      setElements((prev) => [...prev, created]);
      setExpandedId(created.id);
    } catch {
      toast.error("Impossible d'ajouter l'élément");
    }
  };

  const handleDelete = async (id: number) => {
    if (readOnly) return;
    if (!window.confirm('Supprimer cet élément de rédaction ?')) return;
    try {
      await patternService.deleteRedactionElement(id);
      setElements((prev) => prev.filter((el) => el.id !== id));
    } catch {
      toast.error('Suppression impossible');
    }
  };

  const handlePatch = async (id: number, data: Partial<PatternRedactionElement>) => {
    if (readOnly) return;
    try {
      const updated = await patternService.updateRedactionElement(id, data);
      // ne reprendre que les champs envoyés (et leurs libellés) : un autre champ peut être en cours de saisie
      setElements((prev) =>
        prev.map((el) =>
          el.id === id
            ? {
                ...el,
                ...data,
                skills_details: updated.skills_details,
                course_title: updated.course_title,
                video_title: updated.video_title,
                updated_at: updated.updated_at,
              }
            : el
        )
      );
    } catch {
      toast.error('Sauvegarde impossible');
    }
  };

  const handleMove = async (id: number, dir: -1 | 1) => {
    if (readOnly) return;
    const idx = elements.findIndex((el) => el.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= elements.length) return;
    const reordered = [...elements];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setElements(reordered.map((el, i) => ({ ...el, order: i })));
    // Persist new orders
    try {
      await Promise.all(
        reordered.map((el, i) =>
          el.order !== i ? patternService.updateRedactionElement(el.id, { order: i }) : null
        )
      );
    } catch {
      toast.error('Réordonnancement impossible');
    }
  };

  const flagControls = useMemo(
    () => [
      { key: 'has_calculation_zones' as const, label: 'Comporte des zones de calculs à contourer' },
      { key: 'check_equation_chains' as const, label: "Vérifier les chaînes d'égalités" },
      { key: 'check_line_equivalence' as const, label: "Vérifier l'équivalence entre lignes" },
    ],
    []
  );

  const skillModalElement = elements.find((el) => el.id === skillModalElementId) || null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper', borderColor: 'divider' }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            ✍️ Éléments de rédaction ({elements.length})
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={readOnly}
        >
          Ajouter un élément
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Critères que doit vérifier la copie de l'élève. Ils sont envoyés au correcteur IA.
      </Typography>

      <Stack spacing={1} mb={2}>
        {flagControls.map((f) => (
          <FormControlLabel
            key={f.key}
            control={
              <Switch
                size="small"
                checked={!!pattern[f.key]}
                onChange={(e) => onPatternFlagsChange({ [f.key]: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={f.label}
          />
        ))}
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : elements.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Aucun élément de rédaction défini pour ce pattern
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {elements.map((el, idx) => {
            const isExpanded = expandedId === el.id;
            return (
              <Paper key={el.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
                    {idx + 1}.
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 500, color: el.description ? 'text.primary' : 'text.disabled' }}
                    >
                      {el.description || '(sans description)'}
                    </Typography>
                    {(el.skills_details && el.skills_details.length > 0) || el.course_title || el.video_title ? (
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        {el.skills_details?.map((s) => (
                          <Chip key={s.id} label={s.name} size="small" variant="outlined" />
                        ))}
                        {el.course_title && (
                          <Chip label={`📘 ${el.course_title}`} size="small" color="primary" variant="outlined" />
                        )}
                        {el.video_title && (
                          <Chip label={`🎬 ${el.video_title}`} size="small" color="secondary" variant="outlined" />
                        )}
                      </Box>
                    ) : null}
                  </Box>
                  {el.level === 'blocking' && <Chip label="Bloquant" size="small" color="error" />}
                  <Tooltip title="Monter">
                    <span>
                      <IconButton size="small" onClick={() => handleMove(el.id, -1)} disabled={readOnly || idx === 0}>
                        <ArrowUpIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Descendre">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(el.id, 1)}
                        disabled={readOnly || idx === elements.length - 1}
                      >
                        <ArrowDownIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : el.id)}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Tooltip title="Supprimer">
                    <span>
                      <IconButton size="small" onClick={() => handleDelete(el.id)} disabled={readOnly}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {isExpanded && (
                  <Box mt={1.5} display="flex" flexDirection="column" gap={1.5}>
                    <TextField
                      label="Description du critère"
                      placeholder="Ex: L'élève doit identifier a, b et c et écrire a=..., b=..., c=..."
                      multiline
                      minRows={2}
                      value={el.description}
                      onChange={(e) =>
                        setElements((prev) =>
                          prev.map((x) => (x.id === el.id ? { ...x, description: e.target.value } : x))
                        )
                      }
                      onBlur={() => handlePatch(el.id, { description: el.description })}
                      disabled={readOnly}
                      fullWidth
                    />
                    <CriterionLevelSelect
                      value={el.level}
                      onChange={(level) => handlePatch(el.id, { level })}
                      disabled={readOnly}
                    />
                    <TextField
                      label="Consigne pour le correcteur"
                      placeholder="Ex: accepter « delta » ou « Δ » ; le calcul peut être fait de tête"
                      multiline
                      minRows={2}
                      value={el.grader_hint}
                      onChange={(e) =>
                        setElements((prev) =>
                          prev.map((x) => (x.id === el.id ? { ...x, grader_hint: e.target.value } : x))
                        )
                      }
                      onBlur={() => handlePatch(el.id, { grader_hint: el.grader_hint })}
                      disabled={readOnly}
                      fullWidth
                    />
                    <TextField
                      label="Exemple LaTeX/texte attendu"
                      placeholder="Ex: $a = 3,\\quad b = 2,\\quad c = -3$"
                      multiline
                      minRows={2}
                      value={el.example}
                      onChange={(e) =>
                        setElements((prev) =>
                          prev.map((x) => (x.id === el.id ? { ...x, example: e.target.value } : x))
                        )
                      }
                      onBlur={() => handlePatch(el.id, { example: el.example })}
                      disabled={readOnly}
                      fullWidth
                    />
                    <TextField
                      label="Fonction Python optionnelle (exemple dynamique)"
                      placeholder="def example(a, b, c): return f'a={a}, b={b}, c={c}'"
                      multiline
                      minRows={2}
                      value={el.python_function}
                      onChange={(e) =>
                        setElements((prev) =>
                          prev.map((x) => (x.id === el.id ? { ...x, python_function: e.target.value } : x))
                        )
                      }
                      onBlur={() => handlePatch(el.id, { python_function: el.python_function })}
                      disabled={readOnly}
                      fullWidth
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        Savoir-faire :
                      </Typography>
                      {el.skills_details && el.skills_details.length > 0 ? (
                        el.skills_details.map((s) => (
                          <Chip key={s.id} label={s.name} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          aucun
                        </Typography>
                      )}
                      <Button size="small" onClick={() => setSkillModalElementId(el.id)} disabled={readOnly}>
                        Modifier
                      </Button>
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Autocomplete
                        sx={{ flex: 1, minWidth: 240 }}
                        size="small"
                        options={courses}
                        getOptionLabel={(c) => c.title}
                        value={courses.find((c) => c.id === el.course) || null}
                        onChange={(_, value) => handlePatch(el.id, { course: value ? value.id : null })}
                        renderInput={(params) => <TextField {...params} label="Cours associé" />}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        disabled={readOnly}
                      />
                      <Autocomplete
                        sx={{ flex: 1, minWidth: 240 }}
                        size="small"
                        options={videos}
                        getOptionLabel={(v) => v.title}
                        value={videos.find((v) => v.id === el.video) || null}
                        onChange={(_, value) => handlePatch(el.id, { video: value ? value.id : null })}
                        renderInput={(params) => <TextField {...params} label="Vidéo associée" />}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        disabled={readOnly}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {inherited.length > 0 && (
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Hérités des savoir-faire des solutions ({inherited.length}) — à modifier dans l'éditeur du savoir-faire
          </Typography>
          <Stack spacing={0.5}>
            {inherited.map((c) => (
              <Box key={c.id} display="flex" alignItems="center" gap={1}>
                {c.level === 'blocking' && <Chip label="Bloquant" size="small" color="error" />}
                <Typography variant="body2" sx={{ flex: 1 }}>{c.description}</Typography>
                <Chip label={c.owner_skill_name} size="small" variant="outlined" />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {skillModalElement && (
        <SkillSelectionModal
          open={!!skillModalElement}
          onClose={() => setSkillModalElementId(null)}
          initialSkills={skillModalElement.skills.map((sid) => ({ skill_id: sid, weight: 1.0 }))}
          onSave={(picked) => {
            const ids = picked.map((p) => p.skill_id);
            handlePatch(skillModalElement.id, { skills: ids });
            setSkillModalElementId(null);
          }}
        />
      )}
    </Paper>
  );
};

export default PatternRedactionElementsSection;
